import { atom } from 'jotai';

export type ValidationSeverity = 'error' | 'warning';

export type ValidationIssue = {
  nodeId: string;
  field: string | null;
  code: string;
  message: string;
  severity: ValidationSeverity;
};

export type FlowValidationResult = {
  valid: boolean;
  issues: ValidationIssue[];
};

// Main validation result atom
export const $validationResult = atom<FlowValidationResult | null>(null);

// Derived: has any errors (not warnings)
export const $hasValidationErrors = atom((get) => {
  const result = get($validationResult);
  if (!result) return false;
  return result.issues.some((issue) => issue.severity === 'error');
});

// Derived: issues grouped by node ID
export const $issuesByNodeId = atom((get) => {
  const result = get($validationResult);
  if (!result) return new Map<string, ValidationIssue[]>();

  const map = new Map<string, ValidationIssue[]>();
  for (const issue of result.issues) {
    if (!map.has(issue.nodeId)) {
      map.set(issue.nodeId, []);
    }
    const nodeIssues = map.get(issue.nodeId);
    if (nodeIssues) {
      nodeIssues.push(issue);
    }
  }
  return map;
});

// Derived: count of errors per node
export const $errorCountByNodeId = atom((get) => {
  const issuesByNode = get($issuesByNodeId);
  const errorCount = new Map<string, number>();

  for (const [nodeId, issues] of issuesByNode.entries()) {
    const errorIssues = issues.filter((issue) => issue.severity === 'error');
    if (errorIssues.length > 0) {
      errorCount.set(nodeId, errorIssues.length);
    }
  }

  return errorCount;
});

// Derived: count of warnings per node
export const $warningCountByNodeId = atom((get) => {
  const issuesByNode = get($issuesByNodeId);
  const warningCount = new Map<string, number>();

  for (const [nodeId, issues] of issuesByNode.entries()) {
    const warningIssues = issues.filter(
      (issue) => issue.severity === 'warning',
    );
    if (warningIssues.length > 0) {
      warningCount.set(nodeId, warningIssues.length);
    }
  }

  return warningCount;
});
